/* eslint-disable react/jsx-one-expression-per-line */
import * as React from 'react'
import { useState, useCallback, useMemo } from 'react'
import filesize from 'filesize'
import { toast } from 'react-toastify'
import { Grid } from '@mui/material'
import { FileRejection, useDropzone } from 'react-dropzone'
import { EntryImportFileFieldList } from './ImportFileFieldList'
import { useLang } from '~/hooks/useLang'
import './styles.scss'
import Box from '@mui/material/Box'

interface ImportFileFieldProps {
  readonly dispatchId: number | null
  readonly onFileSelected?: (files: readonly FileProps[]) => void
  readonly medias?: readonly FileProps[]
}

export interface FileProps {
  readonly file: File
  readonly type: string
  readonly name: string
  readonly readableSize: string
  readonly image: string | null
}

export function ImportFileFieldWithoutSaveButton({
  onFileSelected,
}: ImportFileFieldProps) {
  const { translate } = useLang()
  const [uploadedFiles, setUploadedFiles] = useState<readonly FileProps[]>([])
  const maxSize = 16000000

  const submitFile = useCallback(async (acceptedFiles: readonly File[]) => {
    const filePromises = acceptedFiles.map((file) => {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve((reader.result as string).split(',')[1])
        reader.onerror = (error) => reject(error)
      })
    })

    try {
      const base64Images = await Promise.all(filePromises)
      const newFiles = acceptedFiles.map((file, index) => ({
        file,
        name: file.name,
        readableSize: filesize(file.size),
        type: file.type ? file.type.split('/')[1] : 'unknown', // Corrigido aqui
        image: base64Images[index],
        medias: undefined,
      }))

      const updatedFiles = [...uploadedFiles, ...newFiles]
      setUploadedFiles(updatedFiles)
      if (onFileSelected) {
        onFileSelected(updatedFiles)
      }
    } catch (error) {
      console.error('Error processing files:', error)
    }
  }, [uploadedFiles, onFileSelected])

  function rejectFiles(fileRejections: readonly FileRejection[]) {
    fileRejections.forEach((fileRejection) => {
      fileRejection.errors.forEach((err) => {
        if (err.code === 'file-invalid-type') {
          toast.error(
            translate('Enter a file of the following types:') +
              ' png, jpg, jpeg'
          )
          return
        } else if (err.code === 'file-too-large') {
          toast.error(
            translate('File is larger than') + ` ${maxSize / 1000000}MB`
          )
          return
        }
      })
    })
  }

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/jpeg': [],
      'image/png': [],
      'image/jpg': [],
    },
    onDropAccepted: submitFile,
    onDropRejected: rejectFiles,
    multiple: true,
    maxSize,
  })

  const handleRemoveFile = useCallback(
    (fileName: string) => {
      const newUploadedFiles = uploadedFiles.filter(
        (file) => file.name !== fileName
      )
      setUploadedFiles(newUploadedFiles)
      if (onFileSelected) {
        onFileSelected(newUploadedFiles)
      }
    },
    [uploadedFiles, onFileSelected]
  )

  const memoizedFiles = useMemo(() => uploadedFiles, [uploadedFiles])

  return (
    <section className="container">
      <Box
        {...getRootProps()}
        sx={{
          position: 'relative',
          display: 'flex',
          gap: '10px',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100px',
          padding: '20px',
          borderRadius: '10px',
          border: '2px dashed #555',
          color: '#444',
          cursor: 'pointer',
        }}
        className="dropzone dropzone-input-size"
      >
        <input {...getInputProps()} />
        <p>{translate('Drag and Drop Image Here')}</p>
      </Box>
      <footer>
        <aside>
          <h4>{translate('Files')}</h4> {/* Já traduzido aqui */}
          {memoizedFiles.map((file) => (
            <EntryImportFileFieldList
              key={file.name}
              file={file}
              onDelete={() => handleRemoveFile(file.name)}
            />
          ))}
        </aside>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <Grid item p={2} />
        </Grid>
      </footer>
    </section>
  )
}
